import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Rocket, Compass, Sparkles, Zap, Flame, Leaf, Copy, Check,
  Twitter, Send, Globe, MessageCircle, Upload, ChevronRight,
  ChevronDown, ArrowLeft, ExternalLink, BadgeCheck, Wallet,
  Share2, ShoppingCart, PieChart, Users, LineChart, Info,
} from "lucide-react";
import {
  DEMO_LAUNCHES, LAUNCH_PACKAGE, LAUNCHPAD_FAQ,
  BUY_BURN_STATS, ECOSYSTEM_STATS, HOW_IT_WORKS,
} from "./data";
import { StickyWalletProvider, useStickyNetwork, buildExplorerUrl, DEFAULT_LOCALNET_RPC } from "./WalletProvider";
import { WalletPanel } from "./WalletPanel";
import { TokenCreator } from "./TokenCreator";
import { ExploreSection } from "./Explore";
import { LaunchFeeTransparencyPanel, BuyBurnPanel } from "./Transparency";
import "./launchpad.css";

const shortAddr = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

function CopyChip({ value, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e?.preventDefault?.();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      className={`sticky-lp-ca-copy ${copied ? "is-copied" : ""}`}
      onClick={onCopy}
      data-testid={testId}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? "Copied" : label}
    </button>
  );
}

/* -------------------------------------------------------------
 * Formatters
 * ----------------------------------------------------------- */
const fmtUsd = (n) => {
  if (n == null) return "—";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + n.toFixed(2);
};
const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const fmtPrice = (n) => {
  if (n == null) return "—";
  if (n >= 1)    return "$" + n.toFixed(4);
  if (n >= 0.01) return "$" + n.toFixed(4);
  return "$" + n.toPrecision(2);
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

/* -------------------------------------------------------------
 * Shell — layout wrapper for all Launchpad routes
 * ----------------------------------------------------------- */
export function LaunchpadShell({ children }) {
  return (
    <StickyWalletProvider>
      <div className="sticky-lp" data-testid="launchpad-shell">
        <div className="sticky-lp-topbar">
          <Link to="/" className="sticky-lp-back" data-testid="lp-back-home">
            <ArrowLeft size={14} />
            <span>Back to STICKY</span>
          </Link>
          <nav className="sticky-lp-subnav" aria-label="Launchpad">
            <Link to="/launchpad" className="sticky-lp-navlink" data-testid="lp-nav-home">Home</Link>
            <Link to="/launchpad/create" className="sticky-lp-navlink" data-testid="lp-nav-create">Create</Link>
            <Link to="/launchpad/explore" className="sticky-lp-navlink" data-testid="lp-nav-explore">Explore</Link>
          </nav>
        </div>
        <div className="sticky-lp-container">{children}</div>
      </div>
    </StickyWalletProvider>
  );
}

/* -------------------------------------------------------------
 * Reusable "Coming Soon" badge
 * ----------------------------------------------------------- */
export function ComingSoonBadge({ label = "Coming Soon" }) {
  return (
    <span className="sticky-lp-soon" data-testid="lp-coming-soon">
      <span className="sticky-lp-soon-dot" />
      {label}
    </span>
  );
}

/* -------------------------------------------------------------
 * Hero
 * ----------------------------------------------------------- */
export function LaunchpadHero() {
  return (
    <section className="sticky-lp-hero" data-testid="lp-hero">
      <ComingSoonBadge label="Coming Soon · Preview" />
      <h1 className="sticky-lp-h1">
        STICKY <span className="sticky-accent">Launchpad</span>
      </h1>
      <p className="sticky-lp-tagline">Launch your token. Build your community.</p>
      <p className="sticky-lp-lede">
        STICKY Launchpad will let creators launch Solana tokens through the STICKY Ecosystem —
        a simple, transparent path from idea to on-chain, powered by the same community that powers STICKY.
      </p>
      <div className="sticky-lp-cta-row">
        <Link to="/launchpad/create" className="sticky-lp-btn is-primary" data-testid="lp-cta-create">
          <Rocket size={16} strokeWidth={2.4} />
          Create Token
        </Link>
        <Link to="/launchpad/explore" className="sticky-lp-btn is-ghost" data-testid="lp-cta-explore">
          <Compass size={16} strokeWidth={2.4} />
          Explore Launches
        </Link>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------
 * Launch Package (fee split)
 * ----------------------------------------------------------- */
export function LaunchPackage() {
  const { priceUsd, splits, includes } = LAUNCH_PACKAGE;
  return (
    <section className="sticky-lp-card sticky-lp-pkg" data-testid="lp-launch-package">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">STICKY <span className="sticky-accent">Launch Package</span></h2>
        <ComingSoonBadge />
      </div>
      <div className="sticky-lp-price">
        <span className="sticky-lp-price-value">${priceUsd}</span>
        <span className="sticky-lp-price-label">Planned launch fee</span>
      </div>

      <div className="sticky-lp-splits" role="list">
        {splits.map((s, i) => (
          <div key={s.label} role="listitem" className={`sticky-lp-split sticky-lp-split-${i}`}>
            <div className="sticky-lp-split-bar" style={{ width: `${s.pct}%` }} />
            <div className="sticky-lp-split-row">
              <span className="sticky-lp-split-label">{s.label}</span>
              <span className="sticky-lp-split-pct">{s.pct}%</span>
            </div>
          </div>
        ))}
        <div className="sticky-lp-split-total">
          <span>Total</span>
          <span>100%</span>
        </div>
      </div>

      <p className="sticky-lp-help">
        <Info size={13} /> Every launch contributes to the STICKY Ecosystem.
      </p>

      <ul className="sticky-lp-includes" data-testid="lp-package-includes">
        {includes.map((i) => (
          <li key={i}><Check size={12} strokeWidth={3} /> {i}</li>
        ))}
      </ul>
      <p className="sticky-lp-note">
        Solana network and third-party infrastructure fees may apply separately.
      </p>
    </section>
  );
}

/* -------------------------------------------------------------
 * How It Works
 * ----------------------------------------------------------- */
export function HowItWorks() {
  return (
    <section className="sticky-lp-card sticky-lp-how" data-testid="lp-how-it-works">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">How it <span className="sticky-accent">works</span></h2>
        <span className="sticky-lp-help-inline">
          <Info size={12} /> Wallet connection & real launches arrive in a later phase.
        </span>
      </div>
      <div className="sticky-lp-how-grid">
        {HOW_IT_WORKS.map((s) => (
          <div key={s.n} className="sticky-lp-step">
            <span className="sticky-lp-step-num">{s.n}</span>
            <h3 className="sticky-lp-step-title">{s.title}</h3>
            <p className="sticky-lp-step-desc">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------
 * Buy & Burn Stats
 * ----------------------------------------------------------- */
export function BuyBurnStats() {
  const s = BUY_BURN_STATS;
  return (
    <section className="sticky-lp-card sticky-lp-bb" data-testid="lp-buyburn">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">
          <Flame size={18} className="sticky-lp-h2-icon" />
          STICKY <span className="sticky-accent">Buy &amp; Burn</span>
        </h2>
        <ComingSoonBadge />
      </div>
      <p className="sticky-lp-body">
        A portion of future STICKY Launchpad fees will be allocated toward purchasing STICKY
        and permanently burning the purchased tokens.
      </p>
      <div className="sticky-lp-stat-row">
        <div className="sticky-lp-stat"><span className="sticky-lp-stat-value">{fmtInt(s.stickyBurned)}</span><span className="sticky-lp-stat-label">STICKY Burned</span></div>
        <div className="sticky-lp-stat"><span className="sticky-lp-stat-value">{fmtInt(s.launches)}</span><span className="sticky-lp-stat-label">Launches</span></div>
        <div className="sticky-lp-stat"><span className="sticky-lp-stat-value">{fmtUsd(s.buyBurnAllocationUsd)}</span><span className="sticky-lp-stat-label">Buy &amp; Burn Allocation</span></div>
      </div>
      <p className="sticky-lp-note">Placeholder values — not connected to the blockchain.</p>
    </section>
  );
}

/* -------------------------------------------------------------
 * Ecosystem Stats
 * ----------------------------------------------------------- */
export function EcosystemStats() {
  const s = ECOSYSTEM_STATS;
  return (
    <section className="sticky-lp-card sticky-lp-eco" data-testid="lp-ecosystem">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">
          <Leaf size={18} className="sticky-lp-h2-icon" />
          STICKY <span className="sticky-accent">Ecosystem</span>
        </h2>
        <ComingSoonBadge />
      </div>
      <p className="sticky-lp-body">
        Part of future Launchpad fees will support development and growth of the STICKY Ecosystem —
        content, tooling, community rewards, and infrastructure.
      </p>
      <div className="sticky-lp-stat-row">
        <div className="sticky-lp-stat"><span className="sticky-lp-stat-value">{fmtUsd(s.ecosystemAllocationUsd)}</span><span className="sticky-lp-stat-label">Ecosystem Allocation</span></div>
        <div className="sticky-lp-stat"><span className="sticky-lp-stat-value">{fmtInt(s.launches)}</span><span className="sticky-lp-stat-label">Launches</span></div>
      </div>
      <p className="sticky-lp-note">Placeholder values — no live data yet.</p>
    </section>
  );
}

/* -------------------------------------------------------------
 * FAQ
 * ----------------------------------------------------------- */
export function LaunchpadFAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="sticky-lp-card sticky-lp-faq" data-testid="lp-faq">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">FAQ</h2>
      </div>
      <ul className="sticky-lp-faq-list">
        {LAUNCHPAD_FAQ.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <li key={item.q} className={`sticky-lp-faq-item ${isOpen ? "is-open" : ""}`}>
              <button
                type="button"
                className="sticky-lp-faq-q"
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                data-testid={`lp-faq-q-${i}`}
              >
                <span>{item.q}</span>
                <ChevronDown size={16} className={`sticky-lp-faq-chev ${isOpen ? "is-open" : ""}`} />
              </button>
              <div className={`sticky-lp-faq-a ${isOpen ? "is-open" : ""}`}>
                <p>{item.a}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------
 * Launch Card (used in explore + homepage teaser)
 * ----------------------------------------------------------- */
export function LaunchCard({ launch }) {
  const t = launch.token;
  const socials = t.socials || {};
  return (
    <Link
      to={`/launchpad/token/${t.slug}`}
      className="sticky-lp-launch"
      data-testid={`lp-launch-${t.slug}`}
    >
      <div className="sticky-lp-launch-head">
        <span className="sticky-lp-launch-logo" aria-hidden="true">{t.logo}</span>
        <div className="sticky-lp-launch-titles">
          <span className="sticky-lp-launch-name">{t.name}</span>
          <span className="sticky-lp-launch-symbol">${t.symbol}</span>
        </div>
        <span className="sticky-lp-launch-badge" title="Launched on STICKY Launchpad">
          <Rocket size={11} strokeWidth={2.6} />
        </span>
      </div>
      <div className="sticky-lp-launch-stats">
        <div><span>Market Cap</span><b>{fmtUsd(launch.marketCapUsd)}</b></div>
        <div><span>Volume</span><b>{fmtUsd(launch.volumeUsd)}</b></div>
        <div><span>Holders</span><b>{fmtInt(launch.holders)}</b></div>
      </div>
      <div className="sticky-lp-launch-foot">
        <span className="sticky-lp-launch-date">{fmtDate(launch.launchedAt)}</span>
        <span className="sticky-lp-launch-socials" aria-hidden="true">
          {socials.x && <Twitter size={12} />}
          {socials.telegram && <Send size={12} />}
          {socials.website && <Globe size={12} />}
          {socials.discord && <MessageCircle size={12} />}
        </span>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------
 * Launch Directory (Explore page)
 * ----------------------------------------------------------- */
export function LaunchDirectory({ launches }) {
  return (
    <div className="sticky-lp-directory" data-testid="lp-directory">
      {launches.map((l) => <LaunchCard key={l.id} launch={l} />)}
    </div>
  );
}

/* -------------------------------------------------------------
 * Token Preview (used by Create form)
 * ----------------------------------------------------------- */
export function TokenPreview({ form }) {
  const socials = form.socials || {};
  return (
    <div className="sticky-lp-card sticky-lp-preview" data-testid="lp-token-preview">
      <div className="sticky-lp-preview-head">
        <span className="sticky-lp-preview-logo">
          {form.logoDataUrl ? (
            <img src={form.logoDataUrl} alt="" />
          ) : (
            <span aria-hidden="true">🚀</span>
          )}
        </span>
        <div>
          <div className="sticky-lp-preview-name">{form.name || "Example Token"}</div>
          <div className="sticky-lp-preview-symbol">${(form.symbol || "EXAMPLE").toUpperCase()}</div>
        </div>
      </div>
      <p className="sticky-lp-preview-desc">
        {form.description || "Short description about the token appears here."}
      </p>
      <div className="sticky-lp-preview-meta">
        <span>Total Supply</span>
        <b>{fmtInt(Number(form.supply) || 1_000_000_000)}</b>
      </div>
      <div className="sticky-lp-preview-meta">
        <span>Decimals</span>
        <b>{form.decimals || 6}</b>
      </div>
      <div className="sticky-lp-preview-socials">
        {socials.x && <a href={socials.x} target="_blank" rel="noopener noreferrer"><Twitter size={12} /> X</a>}
        {socials.telegram && <a href={socials.telegram} target="_blank" rel="noopener noreferrer"><Send size={12} /> Telegram</a>}
        {socials.website && <a href={socials.website} target="_blank" rel="noopener noreferrer"><Globe size={12} /> Website</a>}
        {socials.discord && <a href={socials.discord} target="_blank" rel="noopener noreferrer"><MessageCircle size={12} /> Discord</a>}
      </div>
      <div className="sticky-lp-launched-badge" data-testid="lp-launched-badge">
        <Rocket size={12} /> Launched on STICKY Launchpad
      </div>
      <p className="sticky-lp-note">Preview badge — not yet issued on-chain.</p>
    </div>
  );
}

/* -------------------------------------------------------------
 * Create Token Form
 * ----------------------------------------------------------- */
export function CreateTokenForm() {
  const [form, setForm] = useState({
    name: "",
    symbol: "",
    description: "",
    supply: "1000000000",
    decimals: "6",
    logoDataUrl: "",
    socials: { x: "", telegram: "", website: "", discord: "" },
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("logoDataUrl", String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    // Phase 1 — no submission. This is a preview only.
  };

  return (
    <form className="sticky-lp-form" onSubmit={onSubmit} data-testid="lp-create-form">
      <div className="sticky-lp-card">
        <div className="sticky-lp-card-head">
          <h2 className="sticky-lp-h2">Token <span className="sticky-accent">Information</span></h2>
          <ComingSoonBadge label="Preview · Not On-chain" />
        </div>

        <div className="sticky-lp-form-grid">
          <label className="sticky-lp-field">
            <span>Token Name</span>
            <input
              type="text" placeholder="Example Token" value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="lp-input-name"
            />
          </label>
          <label className="sticky-lp-field">
            <span>Token Symbol</span>
            <input
              type="text" placeholder="EXAMPLE" value={form.symbol} maxLength={12}
              onChange={(e) => set("symbol", e.target.value.toUpperCase())}
              data-testid="lp-input-symbol"
            />
          </label>
          <label className="sticky-lp-field sticky-lp-field-full">
            <span>Token Logo</span>
            <div className="sticky-lp-upload">
              <input type="file" accept="image/*" onChange={onLogo} data-testid="lp-input-logo" />
              <span><Upload size={13} /> Upload image (PNG / JPG / WEBP)</span>
            </div>
          </label>
          <label className="sticky-lp-field sticky-lp-field-full">
            <span>Description</span>
            <textarea
              rows={4} placeholder="What is your token about?" value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="lp-input-desc"
            />
          </label>
          <label className="sticky-lp-field">
            <span>Total Supply</span>
            <input
              type="number" min="1" value={form.supply}
              onChange={(e) => set("supply", e.target.value)}
              data-testid="lp-input-supply"
            />
          </label>
          <label className="sticky-lp-field">
            <span>Decimals</span>
            <input
              type="number" min="0" max="9" value={form.decimals}
              onChange={(e) => set("decimals", e.target.value)}
              data-testid="lp-input-decimals"
            />
          </label>
        </div>
      </div>

      <div className="sticky-lp-card">
        <div className="sticky-lp-card-head">
          <h2 className="sticky-lp-h2">Social <span className="sticky-accent">Links</span></h2>
          <span className="sticky-lp-help-inline">All optional</span>
        </div>
        <div className="sticky-lp-form-grid">
          <label className="sticky-lp-field"><span><Twitter size={12} /> X / Twitter</span>
            <input type="url" placeholder="https://x.com/yourtoken" value={form.socials.x} onChange={(e) => setSocial("x", e.target.value)} data-testid="lp-input-x" /></label>
          <label className="sticky-lp-field"><span><Send size={12} /> Telegram</span>
            <input type="url" placeholder="https://t.me/yourtoken" value={form.socials.telegram} onChange={(e) => setSocial("telegram", e.target.value)} data-testid="lp-input-tg" /></label>
          <label className="sticky-lp-field"><span><Globe size={12} /> Website</span>
            <input type="url" placeholder="https://yourtoken.com" value={form.socials.website} onChange={(e) => setSocial("website", e.target.value)} data-testid="lp-input-web" /></label>
          <label className="sticky-lp-field"><span><MessageCircle size={12} /> Discord</span>
            <input type="url" placeholder="https://discord.gg/yourtoken" value={form.socials.discord} onChange={(e) => setSocial("discord", e.target.value)} data-testid="lp-input-discord" /></label>
        </div>
      </div>

      <TokenPreview form={form} />

      <div className="sticky-lp-card sticky-lp-fee">
        <div className="sticky-lp-card-head">
          <h2 className="sticky-lp-h2">Launch <span className="sticky-accent">Package</span></h2>
          <ComingSoonBadge />
        </div>
        <div className="sticky-lp-fee-row">
          <span className="sticky-lp-fee-price">${LAUNCH_PACKAGE.priceUsd}</span>
          <span className="sticky-lp-fee-label">One-time · Planned fee</span>
        </div>
        <ul className="sticky-lp-includes">
          {LAUNCH_PACKAGE.includes.map((i) => (
            <li key={i}><Check size={12} strokeWidth={3} /> {i}</li>
          ))}
        </ul>
        <p className="sticky-lp-note">Solana network and third-party infrastructure fees may apply separately.</p>
      </div>

      <WalletPanel />

      <TokenCreator form={form} />

      <LaunchFeeTransparencyPanel />
      <BuyBurnPanel />

      <p className="sticky-lp-note sticky-lp-note-center">
        Mainnet is disabled. Localnet + Devnet only. On-chain Metaplex metadata and the $99 payment are not implemented in this phase — token metadata is stored off-chain in the Launchpad registry.
      </p>
    </form>
  );
}

/* -------------------------------------------------------------
 * Token Detail (demo)
 * ----------------------------------------------------------- */
export function TokenDetail({ launch }) {
  const [copied, setCopied] = useState(false);
  const [sharedMsg, setSharedMsg] = useState("");
  const [paymentRec, setPaymentRec] = useState(null);
  const t = launch.token;
  const socials = t.socials || {};
  const contract = t.contract || "";
  const isRemote = !!launch.remote;
  const remoteStatus = launch.remote?.status;
  const banner =
    remoteStatus === "LOCALNET_TEST" ? "LOCALNET TEST — REAL SPL TOKEN"
    : remoteStatus === "DEVNET_TEST" ? "DEVNET TEST — REAL SPL TOKEN"
    : "DEMO TOKEN — LAUNCHPAD NOT LIVE";

  const explorer = (kind, id) => {
    if (!id) return null;
    const netKey = (launch.remote?.network || "").toUpperCase();
    // For LOCALNET, resolve the effective RPC from the same source the
    // WalletProvider uses so the Explorer link points at the user's
    // current ngrok tunnel (not a stale env var).
    let localnetRpc = DEFAULT_LOCALNET_RPC;
    try {
      const saved = typeof window !== "undefined"
        ? window.localStorage.getItem("sticky_launchpad_localnet_rpc")
        : null;
      if (saved && /^https?:\/\//i.test(saved)) localnetRpc = saved;
    } catch { /* ignore */ }
    const netMap = {
      LOCALNET: { explorerCluster: "custom", rpc: localnetRpc },
      DEVNET:   { explorerCluster: "devnet", rpc: "" },
      MAINNET:  { explorerCluster: "mainnet-beta", rpc: "" },
    };
    const n = netMap[netKey] || netMap.DEVNET;
    return buildExplorerUrl(kind, id, n);
  };

  // Fetch the associated launch-fee payment (if any) so we can render the
  // "$99 · LOCALNET TEST · TEST CONFIRMED" receipt on the token page.
  const paymentId = launch.remote?.paymentId;
  useEffect(() => {
    let cancelled = false;
    if (!paymentId) { setPaymentRec(null); return undefined; }
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/launchpad/payments/${paymentId}`);
        if (!r.ok) return;
        const doc = await r.json();
        if (!cancelled) setPaymentRec(doc);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [paymentId]);

  const copyContract = async () => {
    if (!contract) return;
    try {
      await navigator.clipboard.writeText(contract);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* no-op */ }
  };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: t.name, url });
        setSharedMsg("Shared!");
      } else {
        await navigator.clipboard.writeText(url);
        setSharedMsg("Token page link copied!");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        setSharedMsg("Token page link copied!");
      } catch { /* no-op */ }
    }
    setTimeout(() => setSharedMsg(""), 1800);
  };

  return (
    <div className="sticky-lp-detail" data-testid="lp-token-detail">
      <div className="sticky-lp-detail-banner">
        <span>{banner}</span>
      </div>

      <div className="sticky-lp-card sticky-lp-detail-head">
        <div className="sticky-lp-detail-title">
          <span className="sticky-lp-preview-logo sticky-lp-detail-logo" aria-hidden="true">
            {typeof t.logo === "string" && t.logo.startsWith("data:") ? <img src={t.logo} alt="" /> : t.logo}
          </span>
          <div>
            <div className="sticky-lp-detail-name">{t.name}</div>
            <div className="sticky-lp-detail-sym">${t.symbol}</div>
            <span className="sticky-lp-launched-badge is-inline"><Rocket size={11} /> Launched on STICKY Launchpad</span>
          </div>
        </div>
        <p className="sticky-lp-detail-desc">{t.description}</p>

        <div className="sticky-lp-detail-cta">
          <button type="button" className="sticky-lp-btn is-primary" disabled aria-disabled="true" data-testid="lp-detail-buy">
            <ShoppingCart size={14} /> Buy
            <span className="sticky-lp-soon-inline">Soon</span>
          </button>
          {socials.x && <a className="sticky-lp-btn is-ghost" href={socials.x} target="_blank" rel="noopener noreferrer"><Twitter size={14} /> X</a>}
          {socials.telegram && <a className="sticky-lp-btn is-ghost" href={socials.telegram} target="_blank" rel="noopener noreferrer"><Send size={14} /> Telegram</a>}
          {socials.website && <a className="sticky-lp-btn is-ghost" href={socials.website} target="_blank" rel="noopener noreferrer"><Globe size={14} /> Website</a>}
          <button type="button" className="sticky-lp-btn is-ghost" onClick={copyContract} disabled={!contract} data-testid="lp-detail-copy-ca">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy Contract"}
          </button>
          <button type="button" className="sticky-lp-btn is-ghost" onClick={share} data-testid="lp-detail-share">
            <Share2 size={14} /> {sharedMsg || "Share"}
          </button>
        </div>
      </div>

      {isRemote ? (
        <div className="sticky-lp-card">
          <div className="sticky-lp-card-head"><h2 className="sticky-lp-h2">On-chain <span className="sticky-accent">Info</span></h2><ComingSoonBadge label={launch.remote.network} /></div>
          <div className="sticky-lp-detail-metrics">
            <div><span>Supply</span><b>{fmtInt(t.supply)}</b></div>
            <div><span>Decimals</span><b>{t.decimals}</b></div>
            <div><span>Network</span><b>{launch.remote.network}</b></div>
            <div><span>Created</span><b>{fmtDate(launch.launchedAt)}</b></div>
            <div><span>Status</span><b>{launch.remote.status}</b></div>
          </div>

          <div className="sticky-lp-success-row" data-testid="lp-detail-mint-row">
            <span className="sticky-lp-success-label">Mint Address</span>
            <code>{launch.remote.mintAddress}</code>
            <CopyChip value={launch.remote.mintAddress} testId="lp-detail-copy-mint" />
            {explorer("address", launch.remote.mintAddress) && (
              <a href={explorer("address", launch.remote.mintAddress)} target="_blank" rel="noopener noreferrer" className="sticky-lp-success-ext" data-testid="lp-detail-mint-explorer">
                <ExternalLink size={11} /> Explorer
              </a>
            )}
          </div>
          <div className="sticky-lp-success-row" data-testid="lp-detail-creator-row">
            <span className="sticky-lp-success-label">Creator</span>
            <code>{launch.remote.creatorWallet}</code>
            <CopyChip value={launch.remote.creatorWallet} testId="lp-detail-copy-creator" />
          </div>
          <div className="sticky-lp-success-row" data-testid="lp-detail-sig-row">
            <span className="sticky-lp-success-label">Signature</span>
            <code>{launch.remote.signature}</code>
            <CopyChip value={launch.remote.signature} testId="lp-detail-copy-sig" />
            {explorer("tx", launch.remote.signature) && (
              <a href={explorer("tx", launch.remote.signature)} target="_blank" rel="noopener noreferrer" className="sticky-lp-success-ext" data-testid="lp-detail-tx-explorer">
                <ExternalLink size={11} /> View Tx
              </a>
            )}
          </div>

          {paymentRec && (
            <div className="sticky-lp-pay-receipt" data-testid="lp-detail-payment">
              <span>Launch Fee</span>
              <b>${Number(paymentRec.amount || 99).toFixed(2)} · {String(paymentRec.mode || "").replace("_", " ")}</b>
              <em>{String(paymentRec.status || "").replace("_", " ")}</em>
            </div>
          )}
        </div>
      ) : (
        <div className="sticky-lp-card">
          <div className="sticky-lp-card-head"><h2 className="sticky-lp-h2">Market <span className="sticky-accent">Info</span></h2><ComingSoonBadge label="Demo data" /></div>
          <div className="sticky-lp-detail-metrics">
            <div><span>Price</span><b>{fmtPrice(launch.priceUsd)}</b></div>
            <div><span>Market Cap</span><b>{fmtUsd(launch.marketCapUsd)}</b></div>
            <div><span>Volume</span><b>{fmtUsd(launch.volumeUsd)}</b></div>
            <div><span>Liquidity</span><b>{fmtUsd(launch.liquidityUsd)}</b></div>
            <div><span>Holders</span><b>{fmtInt(launch.holders)}</b></div>
            <div><span>Launched</span><b>{fmtDate(launch.launchedAt)}</b></div>
          </div>
        </div>
      )}

      <div className="sticky-lp-card">
        <div className="sticky-lp-card-head"><h2 className="sticky-lp-h2">Chart</h2></div>
        <div className="sticky-lp-chart-placeholder" data-testid="lp-detail-chart">
          <LineChart size={22} />
          <span>Chart preview — activates when Launchpad goes live.</span>
        </div>
      </div>

      <div className="sticky-lp-card">
        <div className="sticky-lp-card-head"><h2 className="sticky-lp-h2">Contract Address</h2></div>
        <div className="sticky-lp-ca" data-testid="lp-detail-ca">
          <code>{contract || "Not yet deployed"}</code>
          {contract && (
            <button type="button" className="sticky-lp-ca-copy" onClick={copyContract}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* =============================================================
 * PAGES
 * =========================================================== */
export function LaunchpadHome() {
  return (
    <LaunchpadShell>
      <LaunchpadHero />
      <LaunchPackage />
      <BuyBurnStats />
      <EcosystemStats />
      <HowItWorks />

      <section className="sticky-lp-card sticky-lp-featured" data-testid="lp-featured">
        <div className="sticky-lp-card-head">
          <h2 className="sticky-lp-h2">Featured (Demo) <span className="sticky-accent">Launches</span></h2>
          <ComingSoonBadge label="Demo · Not Live" />
        </div>
        <LaunchDirectory launches={DEMO_LAUNCHES.slice(0, 3)} />
        <div className="sticky-lp-featured-foot">
          <Link to="/launchpad/explore" className="sticky-lp-btn is-ghost" data-testid="lp-featured-explore">
            <Compass size={14} /> Explore all launches
            <ChevronRight size={14} />
          </Link>
        </div>
      </section>

      <LaunchpadFAQ />
    </LaunchpadShell>
  );
}

export function LaunchpadCreate() {
  return (
    <LaunchpadShell>
      <section className="sticky-lp-hero sticky-lp-hero-sm">
        <ComingSoonBadge label="Preview · No blockchain calls" />
        <h1 className="sticky-lp-h1">Create <span className="sticky-accent">Token</span></h1>
        <p className="sticky-lp-lede">
          Draft your token. Once STICKY Launchpad activates, this same flow will handle the on-chain launch.
        </p>
      </section>
      <CreateTokenForm />
    </LaunchpadShell>
  );
}

export function LaunchpadExplore() {
  return (
    <LaunchpadShell>
      <ExploreSection />
    </LaunchpadShell>
  );
}

export function LaunchpadTokenPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  const demoLaunch = useMemo(
    () => DEMO_LAUNCHES.find((l) => l.token.slug === slug) || null,
    [slug]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (demoLaunch) { setLoading(false); return; }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/launchpad/tokens/${slug}`);
        if (!res.ok) throw new Error("not found");
        const doc = await res.json();
        if (!cancelled) setRemote(doc);
      } catch { /* fall back to first demo */ }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [slug, demoLaunch]);

  const launch = useMemo(() => {
    if (demoLaunch) return demoLaunch;
    if (!remote) return null;
    return {
      id: remote.id,
      token: {
        slug: remote.mintAddress,
        name: remote.name,
        symbol: remote.symbol,
        description: remote.description,
        logo: remote.image || "🚀",
        supply: Number(remote.supply),
        decimals: remote.decimals,
        socials: {
          x: remote.twitter || undefined,
          telegram: remote.telegram || undefined,
          website: remote.website || undefined,
        },
        contract: remote.mintAddress,
      },
      creator: { handle: remote.creatorWallet?.slice(0, 4) + "…" + remote.creatorWallet?.slice(-4), avatar: null },
      status: remote.status,
      marketCapUsd: null,
      volumeUsd: null,
      holders: null,
      launchedAt: remote.createdAt,
      priceUsd: null,
      liquidityUsd: null,
      remote,
    };
  }, [demoLaunch, remote]);

  return (
    <LaunchpadShell>
      <div className="sticky-lp-crumb">
        <button type="button" onClick={() => navigate(-1)} className="sticky-lp-crumb-back">
          <ArrowLeft size={12} /> Back
        </button>
        <span>·</span>
        <Link to="/launchpad/explore">Explore</Link>
        <span>·</span>
        <span>{launch?.token?.symbol || "…"}</span>
      </div>
      {loading && <div className="sticky-lp-card"><p className="sticky-lp-note">Loading token…</p></div>}
      {!loading && !launch && (
        <div className="sticky-lp-card">
          <p className="sticky-lp-body">Token not found. It may not have been indexed yet.</p>
          <Link to="/launchpad/explore" className="sticky-lp-btn is-ghost"><Compass size={12} /> Back to Explore</Link>
        </div>
      )}
      {!loading && launch && <TokenDetail launch={launch} />}
    </LaunchpadShell>
  );
}
